import { AlertCircle, Calendar, Clock, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useOperatingHours } from "@/domains/settings/hooks/use-operating-hours";
import type { CreateSalonTotalCapacityInput, SalonTotalCapacity } from "@/gql/graphql";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import {
  useCreateSalonTotalCapacity,
  useDeleteSalonTotalCapacity,
  useSalonTotalCapacities,
  useUpdateSalonTotalCapacity,
} from "../hooks/use-salon-total-capacities";
import { getVisibleHours, layoutDay } from "../utils/week-layout";

interface TimeRangeLimit {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  maxAppointments: number;
  isActive: boolean;
  daysOfWeek: number[]; // 0-6 for Sunday-Saturday
  icon: React.ReactNode;
  color: string;
}

// Preset time ranges will be translated in the component

// Days of week will be translated in the component

// Pixels per hour in the week view: tall enough for a one-hour rule to show its limit and time
const HOUR_HEIGHT = 44;
// Blocks shorter than this show only their limit; the rest is in the tooltip
const TALL_BLOCK_HEIGHT = 36;

// Helper to convert SalonTotalCapacity to TimeRangeLimit
const convertToTimeRangeLimit = (capacity: SalonTotalCapacity): TimeRangeLimit => {
  return {
    id: capacity.id,
    name: capacity.name || "Unnamed",
    startTime: capacity.startTime || "09:00",
    endTime: capacity.endTime || "17:00",
    maxAppointments: capacity.maxTotal,
    isActive: capacity.isActive,
    daysOfWeek:
      capacity.dayOfWeeks && capacity.dayOfWeeks.length > 0
        ? capacity.dayOfWeeks
        : [0, 1, 2, 3, 4, 5, 6],
    icon: <Clock className="w-4 h-4" />,
    color: capacity.color || "blue",
  };
};

export function TimeRangeLimits() {
  const { t } = useTranslation("settings");
  const { currentSalon } = useSalonStore();
  const createCapacity = useCreateSalonTotalCapacity();
  const updateCapacity = useUpdateSalonTotalCapacity();
  const deleteCapacity = useDeleteSalonTotalCapacity();

  const {
    operatingHours,
    loading: operatingHoursLoading,
    error: operatingHoursError,
  } = useOperatingHours(currentSalon?.id);
  const {
    data,
    loading,
    error: capacitiesError,
    refetch,
  } = useSalonTotalCapacities(currentSalon?.id || "", {
    isActive: true,
  });
  // The week view needs the opening hours for its range, so a failed read is
  // shown instead of silently drawing the whole day.
  const error = capacitiesError ?? operatingHoursError;

  // Translated days of week
  const DAYS_OF_WEEK = [
    {
      value: 0,
      label: t("calendar.sun"),
      short: t("calendar.sunShort"),
      full: t("calendar.sunday"),
    },
    {
      value: 1,
      label: t("calendar.mon"),
      short: t("calendar.monShort"),
      full: t("calendar.monday"),
    },
    {
      value: 2,
      label: t("calendar.tue"),
      short: t("calendar.tueShort"),
      full: t("calendar.tuesday"),
    },
    {
      value: 3,
      label: t("calendar.wed"),
      short: t("calendar.wedShort"),
      full: t("calendar.wednesday"),
    },
    {
      value: 4,
      label: t("calendar.thu"),
      short: t("calendar.thuShort"),
      full: t("calendar.thursday"),
    },
    {
      value: 5,
      label: t("calendar.fri"),
      short: t("calendar.friShort"),
      full: t("calendar.friday"),
    },
    {
      value: 6,
      label: t("calendar.sat"),
      short: t("calendar.satShort"),
      full: t("calendar.saturday"),
    },
  ];

  const [editingLimit, setEditingLimit] = useState<TimeRangeLimit | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Convert backend data to UI format
  const timeRangeLimits = (data?.getSalonTotalCapacities?.capacities || []).map(
    convertToTimeRangeLimit
  );
  const activeLimits = timeRangeLimits.filter((limit) => limit.isActive);
  const { startHour, endHour } = getVisibleHours(operatingHours, activeLimits);
  const visibleHourCount = endHour - startHour;
  const visibleHours = Array.from({ length: visibleHourCount + 1 }, (_, i) => startHour + i);
  const deletingLimit = timeRangeLimits.find((limit) => limit.id === deletingId);

  const formatRuleTime = (time: string) => {
    const [hour, minute = "00"] = time.split(":");
    const hourNumber = Number.parseInt(hour, 10);
    return minute === "00"
      ? t("timeRanges.timeOnTheHour", { hour: hourNumber })
      : t("timeRanges.timeWithMinutes", { hour: hourNumber, minute });
  };

  // One-line rule summary, e.g. "Morning: 9:00–11:00, max 2 bookings"
  const getRuleSummary = (limit: TimeRangeLimit) =>
    t("timeRanges.ruleSummary", {
      name: limit.name,
      start: formatRuleTime(limit.startTime),
      end: formatRuleTime(limit.endTime),
      count: limit.maxAppointments,
    });

  const getRuleDays = (limit: TimeRangeLimit) =>
    limit.daysOfWeek.length === 7
      ? t("timeRanges.everyDay")
      : DAYS_OF_WEEK.filter((day) => limit.daysOfWeek.includes(day.value))
          .map((day) => day.short)
          .join(", ");

  const getColorClasses = (color: string) => {
    const colors = {
      yellow:
        "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
      orange:
        "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800",
      green:
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
      blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
      purple:
        "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800",
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const handleSaveLimit = async (limit: TimeRangeLimit) => {
    if (!currentSalon?.id) return;

    try {
      const input: CreateSalonTotalCapacityInput = {
        name: limit.name,
        startTime: limit.startTime,
        endTime: limit.endTime,
        maxTotal: limit.maxAppointments,
        dayOfWeeks: limit.daysOfWeek.length === 7 ? [] : limit.daysOfWeek,
        isActive: limit.isActive,
        priority: 0,
        color: limit.color || "blue",
      };

      if (editingLimit) {
        await updateCapacity.execute({
          salonId: currentSalon.id,
          capacityId: limit.id,
          input,
        });
      } else {
        await createCapacity.execute({
          salonId: currentSalon.id,
          input,
        });
      }

      setEditingLimit(null);
      setIsCreating(false);
      refetch();
    } catch (error) {
      console.error("Failed to save capacity:", error);
    }
  };

  const handleDeleteLimit = async (id: string) => {
    if (!currentSalon?.id) return;

    try {
      await deleteCapacity.execute({
        salonId: currentSalon.id,
        capacityId: id,
      });
      setDeletingId(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete capacity:", error);
    }
  };

  if (loading || operatingHoursLoading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          <CenteredSpinner />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          <div className="text-center text-destructive">
            <p>
              {capacitiesError ? t("timeRanges.loadError") : t("timeRanges.openingHoursLoadError")}
            </p>
            <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <CardTitle className="flex items-center text-lg lg:text-xl">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
              <span className="truncate">{t("timeRanges.title")}</span>
            </CardTitle>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              {t("timeRanges.description")}
            </p>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <Dialog open={isCreating} onOpenChange={setIsCreating}>
              <DialogTrigger asChild>
                <Button size="sm" className="shrink-0">
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{t("timeRanges.addLimit")}</span>
                  <span className="sm:hidden">{t("actions.add")}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <TimeRangeLimitForm
                  limit={null}
                  onSave={handleSaveLimit}
                  onCancel={() => setIsCreating(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-2 sm:px-4 pb-6">
        {/* Edit Dialog */}
        <Dialog open={!!editingLimit} onOpenChange={(open) => !open && setEditingLimit(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {editingLimit && (
              <TimeRangeLimitForm
                limit={editingLimit}
                onSave={handleSaveLimit}
                onCancel={() => setEditingLimit(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("timeRanges.deleteTitle")}</DialogTitle>
              <DialogDescription>
                {deletingLimit && (
                  <span className="block font-medium text-foreground mb-1">
                    {getRuleSummary(deletingLimit)}
                  </span>
                )}
                {t("timeRanges.deleteDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end space-x-2 mt-4">
              <Button variant="outline" onClick={() => setDeletingId(null)}>
                {t("actions.cancel")}
              </Button>
              <Button
                variant="destructive"
                disabled={deleteCapacity.loading}
                onClick={() => deletingId && handleDeleteLimit(deletingId)}
              >
                {deleteCapacity.loading ? t("timeRanges.deleting") : t("actions.delete")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Without a rule nothing limits advanced mode, so make that hard to miss. */}
        {activeLimits.length === 0 && (
          <div
            role="alert"
            className="mb-6 flex flex-col gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4 dark:border-amber-800 dark:bg-amber-950/30"
          >
            <div className="flex items-start gap-2 sm:gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 sm:h-5 sm:w-5 dark:text-amber-400" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                  {t("timeRanges.emptyWarning.title")}
                </p>
                <p className="text-xs text-amber-800 sm:text-sm dark:text-amber-200">
                  {t("timeRanges.emptyWarning.description")}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="shrink-0 self-start sm:self-auto"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t("timeRanges.emptyWarning.action")}
            </Button>
          </div>
        )}

        {/* Week Calendar View */}
        <div className="mb-8">
          <h4 className="text-xs sm:text-sm font-medium mb-4">{t("capacity.weeklyLabel")}</h4>
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <div className="min-w-[600px] sm:min-w-[800px]">
              {/* Day headers */}
              <div className="ml-12 sm:ml-16 grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
                {DAYS_OF_WEEK.map((day) => {
                  const dayLimitCount = activeLimits.filter((limit) =>
                    limit.daysOfWeek.includes(day.value)
                  ).length;
                  const isWeekend = day.value === 0 || day.value === 6;

                  return (
                    <div
                      key={day.value}
                      className={cn(
                        "text-center font-medium text-[10px] sm:text-sm p-1 sm:p-2 rounded-md",
                        isWeekend &&
                          "text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-900/20"
                      )}
                    >
                      <div className="hidden sm:block">{day.full}</div>
                      <div className="sm:hidden">{day.short}</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">
                        {dayLimitCount}{" "}
                        {dayLimitCount !== 1 ? t("timeRanges.limits") : t("timeRanges.limit")}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Time axis, hour lines and blocks share one box so they line up */}
              <div
                className="relative flex"
                style={{ height: `${visibleHourCount * HOUR_HEIGHT}px` }}
              >
                <div className="relative w-12 sm:w-16 shrink-0">
                  {visibleHours.map((hour) => (
                    <div
                      key={`time-label-${hour}`}
                      className="absolute w-full -translate-y-1/2 text-right pr-1 sm:pr-2 text-[10px] sm:text-xs text-muted-foreground"
                      style={{ top: `${(hour - startHour) * HOUR_HEIGHT}px` }}
                    >
                      {hour.toString().padStart(2, "0")}:00
                    </div>
                  ))}
                </div>

                <TooltipProvider>
                  <div className="grid flex-1 grid-cols-7 gap-1 sm:gap-2">
                    {DAYS_OF_WEEK.map((day) => {
                      const dayBlocks = layoutDay(
                        activeLimits.filter((limit) => limit.daysOfWeek.includes(day.value))
                      );
                      const isWeekend = day.value === 0 || day.value === 6;

                      return (
                        <div
                          key={day.value}
                          data-testid={`week-day-${day.value}`}
                          className={cn(
                            "relative border rounded-lg",
                            "border-gray-200 dark:border-gray-700",
                            isWeekend && "bg-blue-50/20 dark:bg-blue-900/10"
                          )}
                        >
                          {/* Hour grid lines between the column's top and bottom borders */}
                          {visibleHours.slice(1, -1).map((hour) => (
                            <div
                              key={`grid-${hour}`}
                              className="absolute inset-x-0 border-t border-gray-100 dark:border-gray-800"
                              style={{ top: `${(hour - startHour) * HOUR_HEIGHT}px` }}
                            />
                          ))}

                          {/* Time range blocks, overlapping rules side by side */}
                          {dayBlocks.map(({ item: limit, start, end, lane, lanes }) => {
                            const height = ((end - start) / 60) * HOUR_HEIGHT;
                            const summary = `${limit.name} · ${limit.startTime} - ${limit.endTime} · ${t("timeRanges.max")}: ${limit.maxAppointments}`;

                            return (
                              <Tooltip key={limit.id}>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label={summary}
                                    onClick={() => setEditingLimit(limit)}
                                    className={cn(
                                      "absolute overflow-hidden rounded-md border-2 px-1 py-0.5 sm:px-1.5 text-left group hover:shadow-lg hover:z-10 transition-all cursor-pointer",
                                      getColorClasses(limit.color)
                                    )}
                                    style={{
                                      top: `${((start - startHour * 60) / 60) * HOUR_HEIGHT}px`,
                                      height: `${height}px`,
                                      left: `calc(${(lane / lanes) * 100}% + 2px)`,
                                      width: `calc(${100 / lanes}% - 4px)`,
                                    }}
                                  >
                                    <div className="text-[9px] sm:text-xs font-semibold leading-tight truncate">
                                      {t("timeRanges.max")}: {limit.maxAppointments}
                                    </div>
                                    {height >= TALL_BLOCK_HEIGHT && (
                                      <div className="text-[9px] sm:text-xs leading-tight opacity-80 truncate">
                                        {limit.startTime} - {limit.endTime}
                                      </div>
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{summary}</TooltipContent>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </TooltipProvider>
              </div>
            </div>
          </div>

          {/* Rule list with inline summaries */}
          {activeLimits.length > 0 && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-muted rounded-lg">
              <h4 className="text-xs sm:text-sm font-medium mb-2 sm:mb-3">
                {t("timeRanges.timeRangeLimitsColon")}
              </h4>
              <ul className="space-y-2">
                {activeLimits.map((limit) => (
                  <li
                    key={limit.id}
                    className="flex items-center gap-2 sm:gap-3 rounded-md bg-background px-2 sm:px-3 py-2"
                  >
                    <div
                      className={cn(
                        "w-3 h-3 sm:w-4 sm:h-4 rounded border-2 flex-shrink-0",
                        getColorClasses(limit.color)
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium break-words">
                        {getRuleSummary(limit)}
                      </p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        {getRuleDays(limit)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 flex-shrink-0"
                      aria-label={t("actions.edit")}
                      onClick={() => setEditingLimit(limit)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 flex-shrink-0 text-destructive hover:text-destructive"
                      aria-label={t("actions.delete")}
                      onClick={() => setDeletingId(limit.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1 text-xs sm:text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-100">
                {t("timeRanges.howTimeRangeLimitsWork")}
              </p>
              <ul className="space-y-1 text-amber-800 dark:text-amber-200 text-[10px] sm:text-xs">
                <li>• {t("timeRanges.timeRangeLimitsHelp1")}</li>
                <li>• {t("timeRanges.timeRangeLimitsHelp3")}</li>
                <li>• {t("timeRanges.timeRangeLimitsHelp4")}</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Form Component for Creating/Editing Time Range Limits
function TimeRangeLimitForm({
  limit,
  onSave,
  onCancel,
}: {
  limit: TimeRangeLimit | null;
  onSave: (limit: TimeRangeLimit) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation("settings");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; time?: string; days?: string }>({});

  // Translated days of week
  const DAYS_OF_WEEK = [
    {
      value: 0,
      label: t("calendar.sun"),
      short: t("calendar.sunShort"),
      full: t("calendar.sunday"),
    },
    {
      value: 1,
      label: t("calendar.mon"),
      short: t("calendar.monShort"),
      full: t("calendar.monday"),
    },
    {
      value: 2,
      label: t("calendar.tue"),
      short: t("calendar.tueShort"),
      full: t("calendar.tuesday"),
    },
    {
      value: 3,
      label: t("calendar.wed"),
      short: t("calendar.wedShort"),
      full: t("calendar.wednesday"),
    },
    {
      value: 4,
      label: t("calendar.thu"),
      short: t("calendar.thuShort"),
      full: t("calendar.thursday"),
    },
    {
      value: 5,
      label: t("calendar.fri"),
      short: t("calendar.friShort"),
      full: t("calendar.friday"),
    },
    {
      value: 6,
      label: t("calendar.sat"),
      short: t("calendar.satShort"),
      full: t("calendar.saturday"),
    },
  ];

  const [formData, setFormData] = useState<Partial<TimeRangeLimit>>(
    limit || {
      name: "",
      startTime: "09:00",
      endTime: "12:00",
      maxAppointments: 50,
      isActive: true,
      daysOfWeek: [1, 2, 3, 4, 5], // Default to weekdays
      icon: <Clock className="w-4 h-4" />,
      color: "blue",
    }
  );

  const toggleDay = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek?.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...(prev.daysOfWeek || []), day].sort(),
    }));
  };

  const handleSubmit = async () => {
    // Validate form
    const newErrors: { name?: string; time?: string; days?: string } = {};

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = t("timeRanges.errors.nameRequired");
    }

    if (!formData.startTime || !formData.endTime) {
      newErrors.time = t("timeRanges.errors.timeRequired");
    } else if (formData.startTime >= formData.endTime) {
      newErrors.time = t("timeRanges.errors.endAfterStart");
    }

    if (!formData.daysOfWeek || formData.daysOfWeek.length === 0) {
      newErrors.days = t("timeRanges.errors.daysRequired");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);
    try {
      await onSave(formData as TimeRangeLimit);
    } catch (error) {
      console.error("Failed to save:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {limit ? t("timeRanges.editTimeRangeLimit") : t("timeRanges.createTimeRangeLimit")}
        </DialogTitle>
        <DialogDescription>{t("timeRanges.setMaximumDescription")}</DialogDescription>
      </DialogHeader>

      <div className="space-y-6 mt-4">
        {/* Name */}
        <div className="space-y-2">
          <Label>{t("timeRanges.name")} *</Label>
          <Input
            type="text"
            placeholder={t("timeRanges.namePlaceholder")}
            value={formData.name}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, name: e.target.value }));
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            className={errors.name ? "border-destructive" : ""}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
        </div>

        {/* Time Range */}
        <div className="space-y-2">
          <Label>Time Range *</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Input
                type="time"
                value={formData.startTime}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, startTime: e.target.value }));
                  if (errors.time) setErrors((prev) => ({ ...prev, time: undefined }));
                }}
                className={cn("bg-background", errors.time && "border-destructive")}
              />
              <p className="text-xs text-muted-foreground">{t("timeRanges.startTime")}</p>
            </div>
            <div className="space-y-2">
              <Input
                type="time"
                value={formData.endTime}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, endTime: e.target.value }));
                  if (errors.time) setErrors((prev) => ({ ...prev, time: undefined }));
                }}
                className={cn("bg-background", errors.time && "border-destructive")}
              />
              <p className="text-xs text-muted-foreground">{t("timeRanges.endTime")}</p>
            </div>
          </div>
          {errors.time && <p className="text-sm text-destructive">{errors.time}</p>}
        </div>

        {/* Max Appointments */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{t("timeRanges.maximumAppointments")}</Label>
            <Input
              type="number"
              min={0}
              max={200}
              step={1}
              value={formData.maxAppointments ?? 50}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 0 && value <= 200) {
                  setFormData((prev) => ({ ...prev, maxAppointments: value }));
                }
              }}
              className="w-20 text-center"
            />
          </div>
          <Slider
            value={[formData.maxAppointments ?? 50]}
            onValueChange={([value]) =>
              setFormData((prev) => ({ ...prev, maxAppointments: value }))
            }
            min={0}
            max={200}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0 ({t("timeRanges.veryLimited")})</span>
            <span>100 ({t("timeRanges.busy")})</span>
            <span>200 ({t("timeRanges.maximum")})</span>
          </div>
        </div>

        {/* Days of Week */}
        <div className="space-y-2">
          <Label>{t("timeRanges.applyToDays")} *</Label>
          <div className="flex space-x-1">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => {
                  toggleDay(day.value);
                  if (errors.days) setErrors((prev) => ({ ...prev, days: undefined }));
                }}
                className={cn(
                  "w-10 h-10 rounded-md flex items-center justify-center text-sm font-medium transition-colors",
                  formData.daysOfWeek?.includes(day.value)
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted-foreground/20",
                  errors.days ? "ring-2 ring-destructive" : ""
                )}
              >
                {day.label}
              </button>
            ))}
          </div>
          {errors.days && <p className="text-sm text-destructive">{errors.days}</p>}
        </div>

        {/* Color Selection */}
        <div className="space-y-2">
          <Label>{t("timeRanges.visualColor")}</Label>
          <div className="flex space-x-2">
            {["yellow", "orange", "green", "blue", "purple"].map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, color }))}
                className={cn(
                  "w-10 h-10 rounded-md border-2 transition-all",
                  formData.color === color ? "ring-2 ring-offset-2 ring-primary" : "",
                  getColorClasses(color)
                )}
              />
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving
              ? limit
                ? "Updating..."
                : "Creating..."
              : limit
                ? t("timeRanges.saveChanges")
                : t("timeRanges.createLimit")}
          </Button>
        </div>
      </div>
    </>
  );
}

function getColorClasses(color: string) {
  const colors = {
    yellow:
      "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
    orange:
      "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800",
    green:
      "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
    purple:
      "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  };
  return colors[color as keyof typeof colors] || colors.blue;
}
